/** Texts of the NY-Opening-Range model (the New York opening range breakout): its tuning, its summary, its chart, its checks. Each key in both languages. */
export const nyorb = {
  en: {
    "no.name": "NY Opening Range",
    "no.tune": "Tuning",
    "no.title": "NY Opening Range tuning",
    "no.about.title": "NY Opening Range – the strategy",
    "no.about.intro": "The New York opening range breakout on gold 5M: the first 15 or 30 minutes after 09:30 New York set the day’s range; the first strong close beyond it until 11:30 is the day’s one trade.",
    "no.about.steps": "Times in New York’s wall clock, daylight saving time included.\nOpening range: high and low of the 5M bars from 09:30 to 09:45 (or 10:00). It counts when its height lies between 0.5 and 3 ATR(14, 5M) of the last bar before 09:30.\nBreakout: from the range’s end until 11:30 the first 5M bar that closes above the range high (buy) or below the range low (sell) with a body of at least 0.3 range heights. Only the first breakout of the day counts.\nEntry: at the next bar’s open. With “gap” on only when the breakout bar leaves a gap (buy: its low above the high of the bar two before) — a limit at that high, valid for 6 bars.\nStop: below the lower low of the breakout bar and the bar before, 0.1 ATR further (sell mirrored). Target: the tuning’s R multiple. Flat at the latest with the close at 15:55 New York.\nZones: the breakout area 0.1 ATR beyond the range’s edge, with the gap entry the gap itself.",
    "no.about.evidence": "Measured (round 45, gold 5M 2010 – 10/2026, bid/ask, $0.07 commission, $0.10 slippage, PREREG45): negative after costs in every setting. Chosen setting (3 R, 15-minute range, market entry): out of sample 2023–2026 −0.15 R per trade (n 627, profit factor 0.78), before costs +0.01 R; 2010–2026 −0.21 R. No edge — discarded. Shown for analysis only.",
    "no.r": "{n} R",
    "no.min": "{n} min",
    "no.target": "Target",
    "no.orMinutes": "Opening range",
    "no.fvg": "Entry only at a gap",
    "no.preset.1": "Standard",
    "no.preset.2": "30-minute range",
    "no.preset.3": "Entry at the gap",
    "no.frames": "5M · range {n} min from 09:30 NY",
    "no.row.range": "Opening range",
    "no.row.status": "Now",
    "no.range.none": "not yet (from 09:30 New York)",
    "no.range.value": "{low} – {high} · {height} ATR",
    "no.range.unfit": "{low} – {high} · {height} ATR — outside 0.5–3 ATR, no trade",
    "no.status.before": "waiting for the opening range",
    "no.status.window": "waiting for a breakout until 11:30 New York",
    "no.status.closed": "no setup today",
    "no.status.taken": "today’s breakout came",
    "no.role.active": "active",
    "no.role.next": "next",
    "no.kind.breakout": "breakout area",
    "no.kind.gap": "gap",
    "no.step.range": "Range stands",
    "no.step.rangeWait": "from 09:30 NY",
    "no.step.rangeUnfit": "{height} ATR: outside 0.5–3",
    "no.step.break": "Breakout with body",
    "no.step.breakOther": "the day went to the other side",
    "no.step.windowClosed": "window closed (11:30 NY)",
    "no.step.breakHow": "close beyond, body ≥ 0.3 range",
    "no.step.retest": "Retest of the gap",
    "no.step.noGap": "no gap: no trade",
    "no.step.expired": "not reached in 6 bars",
    "no.step.retestHow": "limit at the gap’s edge",
    "no.step.entry": "Entry",
    "no.step.entryNext": "at the next open",
    "no.tip.market.long": "Buy at the next open",
    "no.tip.market.short": "Sell at the next open",
    "no.tip.limit.long": "Buy limit at {price} until {time}",
    "no.tip.limit.short": "Sell limit at {price} until {time}",
    "no.tip.running.long": "Buy running since {time}",
    "no.tip.running.short": "Sell running since {time}",
    "no.tip.won": "Today’s trade reached its target",
    "no.tip.lost": "Today’s trade was stopped",
    "no.tip.flat": "Today’s trade was closed at 15:55",
    "no.tip.expired": "The limit expired unfilled",
    "no.tip.waitLong": "waiting for a close above {price}",
    "no.tip.waitShort": "waiting for a close below {price}",
    "no.fig.entry": "Entry",
    "no.fig.limit": "Limit",
    "no.fig.stop": "Stop",
    "no.fig.target": "Target",
    "no.legend": "One trade a day: the first breakout. Zones: 0.1 ATR beyond the range’s edge; with the gap entry the gap.",
    "no.chart.range": "Opening range",
    "no.chart.high": "Range high",
    "no.chart.low": "Range low",
    "no.zone.long": "BUY ZONE {price} · {kind}",
    "no.zone.short": "SELL ZONE {price} · {kind}",
    "no.chart.entry": "Entry {arrow}",
    "no.chart.limit": "Limit {arrow}",
    "no.chart.stop": "Stop",
    "no.chart.target": "Target",
    "no.chart.break": "Breakout {arrow}",
    "no.chart.breakHint": "NY Opening Range: the day’s first close beyond the opening range with a body of at least 0.3 range heights.",
    "chk.noRange": "Range fits",
    "chk.noRange.hint": "NY Opening Range. ✓ once today’s opening range (from 09:30 New York) stands and its height lies between 0.5 and 3 ATR(14, 5M) of the bar before 09:30.",
    "chk.noWindow": "Window open",
    "chk.noWindow.hint": "NY Opening Range. ✓ while the range fits, no breakout came yet and the next bar still closes by 11:30 New York.",
    "chk.noBreak": "Breakout",
    "chk.noBreak.hint": "NY Opening Range. ✓ on its side once today’s first breakout came: a close beyond the range with a body of at least 0.3 range heights.",
    "chk.noSetup": "Setup",
    "chk.noSetup.hint": "NY Opening Range. ✓ on its side while today’s entry waits (the next open, or the limit at the gap) or its trade runs.",
  },
  de: {
    "no.name": "NY-Opening-Range",
    "no.tune": "Feintuning",
    "no.title": "NY-Opening-Range-Feintuning",
    "no.about.title": "NY-Opening-Range – die Strategie",
    "no.about.intro": "Der Ausbruch aus der New Yorker Eröffnungsspanne auf Gold 5M: Die ersten 15 oder 30 Minuten ab 09:30 New York legen die Spanne des Tages fest; der erste kräftige Schluss außerhalb bis 11:30 ist der eine Trade des Tages.",
    "no.about.steps": "Zeiten nach New Yorker Uhr, mit Sommerzeit.\nOpening Range: Hoch und Tief der 5M-Kerzen von 09:30 bis 09:45 (bzw. 10:00). Sie zählt, wenn ihre Höhe zwischen 0,5 und 3 ATR(14, 5M) der letzten Kerze vor 09:30 liegt.\nAusbruch: vom Ende der Range bis 11:30 die erste 5M-Kerze, die über dem Range-Hoch (Kauf) bzw. unter dem Range-Tief (Verkauf) schließt, mit einem Körper von mindestens 0,3 Range-Höhen. Nur der erste Ausbruch des Tages zählt.\nEinstieg: zur Eröffnung der nächsten Kerze. Mit „Lücke“ an nur, wenn die Ausbruchskerze eine Lücke lässt (Kauf: ihr Tief über dem Hoch der vorletzten Kerze) — ein Limit an diesem Hoch, gültig 6 Kerzen.\nStop: unter dem tieferen Tief von Ausbruchskerze und Kerze davor, 0,1 ATR weiter (Verkauf gespiegelt). Ziel: das R-Vielfache des Feintunings. Spätestens mit dem Schluss um 15:55 New York flach.\nZonen: der Ausbruchsbereich 0,1 ATR jenseits der Range-Kante, beim Einstieg an der Lücke die Lücke selbst.",
    "no.about.evidence": "Gemessen (Runde 45, Gold 5M 2010 – 10/2026, Geld/Brief, 0,07 $ Kommission, 0,10 $ Slippage, PREREG45): in jeder Einstellung nach Kosten negativ. Gewählte Einstellung (3 R, Range 15 min, Einstieg Markt): außerhalb 2023–2026 −0,15 R je Trade (n 627, Profit-Faktor 0,78), vor Kosten +0,01 R; 2010–2026 −0,21 R. Kein Vorteil – verworfen. Nur zur Analyse.",
    "no.r": "{n} R",
    "no.min": "{n} Min.",
    "no.target": "Ziel",
    "no.orMinutes": "Opening Range",
    "no.fvg": "Einstieg nur an Lücke",
    "no.preset.1": "Standard",
    "no.preset.2": "30-Minuten-Range",
    "no.preset.3": "Einstieg an der Lücke",
    "no.frames": "5M · Range {n} Min. ab 09:30 NY",
    "no.row.range": "Opening Range",
    "no.row.status": "Jetzt",
    "no.range.none": "noch nicht (ab 09:30 New York)",
    "no.range.value": "{low} – {high} · {height} ATR",
    "no.range.unfit": "{low} – {high} · {height} ATR — außerhalb 0,5–3 ATR, kein Handel",
    "no.status.before": "wartet auf die Opening Range",
    "no.status.window": "wartet auf einen Ausbruch bis 11:30 New York",
    "no.status.closed": "heute kein Setup",
    "no.status.taken": "der Ausbruch des Tages kam",
    "no.role.active": "aktiv",
    "no.role.next": "nächste",
    "no.kind.breakout": "Ausbruchsbereich",
    "no.kind.gap": "Lücke",
    "no.step.range": "Range steht",
    "no.step.rangeWait": "ab 09:30 NY",
    "no.step.rangeUnfit": "{height} ATR: außerhalb 0,5–3",
    "no.step.break": "Ausbruch mit Körper",
    "no.step.breakOther": "der Tag ging an die andere Seite",
    "no.step.windowClosed": "Fenster zu (11:30 NY)",
    "no.step.breakHow": "Schluss außerhalb, Körper ≥ 0,3 Range",
    "no.step.retest": "Retest der Lücke",
    "no.step.noGap": "keine Lücke: kein Trade",
    "no.step.expired": "in 6 Kerzen nicht erreicht",
    "no.step.retestHow": "Limit an der Kante der Lücke",
    "no.step.entry": "Einstieg",
    "no.step.entryNext": "zur nächsten Eröffnung",
    "no.tip.market.long": "Kaufen zur nächsten Eröffnung",
    "no.tip.market.short": "Verkaufen zur nächsten Eröffnung",
    "no.tip.limit.long": "Kauf-Limit bei {price} bis {time}",
    "no.tip.limit.short": "Verkauf-Limit bei {price} bis {time}",
    "no.tip.running.long": "Kauf läuft seit {time}",
    "no.tip.running.short": "Verkauf läuft seit {time}",
    "no.tip.won": "Der Trade des Tages erreichte sein Ziel",
    "no.tip.lost": "Der Trade des Tages wurde ausgestoppt",
    "no.tip.flat": "Der Trade des Tages wurde um 15:55 geschlossen",
    "no.tip.expired": "Das Limit verfiel ungefüllt",
    "no.tip.waitLong": "wartet auf einen Schluss über {price}",
    "no.tip.waitShort": "wartet auf einen Schluss unter {price}",
    "no.fig.entry": "Einstieg",
    "no.fig.limit": "Limit",
    "no.fig.stop": "Stop",
    "no.fig.target": "Ziel",
    "no.legend": "Ein Trade je Tag: der erste Ausbruch. Zonen: 0,1 ATR jenseits der Range-Kante; beim Einstieg an der Lücke die Lücke.",
    "no.chart.range": "Opening Range",
    "no.chart.high": "Range-Hoch",
    "no.chart.low": "Range-Tief",
    "no.zone.long": "BUY ZONE {price} · {kind}",
    "no.zone.short": "SELL ZONE {price} · {kind}",
    "no.chart.entry": "Einstieg {arrow}",
    "no.chart.limit": "Limit {arrow}",
    "no.chart.stop": "Stop",
    "no.chart.target": "Ziel",
    "no.chart.break": "Ausbruch {arrow}",
    "no.chart.breakHint": "NY-Opening-Range: der erste Schluss des Tages außerhalb der Opening Range mit einem Körper von mindestens 0,3 Range-Höhen.",
    "chk.noRange": "Range passt",
    "chk.noRange.hint": "NY-Opening-Range. ✓, sobald die heutige Opening Range (ab 09:30 New York) steht und ihre Höhe zwischen 0,5 und 3 ATR(14, 5M) der Kerze vor 09:30 liegt.",
    "chk.noWindow": "Fenster offen",
    "chk.noWindow.hint": "NY-Opening-Range. ✓, solange die Range passt, noch kein Ausbruch kam und die nächste Kerze bis 11:30 New York schließt.",
    "chk.noBreak": "Ausbruch",
    "chk.noBreak.hint": "NY-Opening-Range. ✓ auf seiner Seite, sobald der erste Ausbruch des Tages kam: ein Schluss außerhalb der Range mit einem Körper von mindestens 0,3 Range-Höhen.",
    "chk.noSetup": "Setup",
    "chk.noSetup.hint": "NY-Opening-Range. ✓ auf seiner Seite, solange der Einstieg des Tages wartet (die nächste Eröffnung oder das Limit an der Lücke) oder sein Trade läuft.",
  },
};
